import React, { useState, useEffect } from "react";
import { Button, Modal, Form, Row, Col, Accordion } from "react-bootstrap";
import { Link } from "react-router-dom";
import useApi from "../../hooks/useApi";
import useApp from "../../hooks/useApp";
import { useQuery } from "react-query";
import usePagination from "../../hooks/usePagination";
import Swal from "sweetalert2";
import useReload from "../../hooks/useReload";
import moment from "moment";

const ClientPolicyAttachment = (props) => {
  const [policy, setPolicy] = useState(props?.policy || {});
  const { hideAction = false } = props;

  const { api, abortSignal, auth } = useApi();
  const { showAlert } = useApp();
  const { searchParamString } = usePagination();

  const { toggleReload, reload } = useReload();

  const [showAttachmentForm, setShowAttachmentForm] = useState(false);
  const handleCloseAttachmentForm = () => setShowAttachmentForm(false);
  const handleShowAttachmentForm = () => setShowAttachmentForm(true);

  const [policyAttachment, setPolicyAttachment] = useState({
    name: "",
    attachment: "",
    visible: false,
  });

  const saveAttachment = async (e) => {
    e.preventDefault();

    const data = {
      name: policyAttachment.name,
      visible: policyAttachment.visible,
    };

    const response = await api.update(
      `/policy-attachments/${policyAttachment.id}`,
      data
    );
    if (response.ok) {
      setPolicyAttachment({
        name: "",
        attachment: "",
        visible: true,
      });
      showAlert("Policy attachment updated", "success");
      handleCloseAttachmentForm();
      toggleReload();
    }
  };

  const fetchAttachments = async (searchString) => {
    try {
      const url = `/policy-attachments/policyItem/${policy.id}${searchString}`;
      const { data } = await api.list(url);
      return data;
    } catch (error) {
      throw new Error("failed to fetch records");
    }
  };

  const { data: attachments } = useQuery(
    ["policyAttachments", searchParamString, auth, policy, reload],
    ({ queryKey }) => fetchAttachments(queryKey[1])
  );

  const handleEditAttachment = (attachment = {}) => {
    setPolicyAttachment(attachment);
    handleShowAttachmentForm();
  };

  const handleDeleteAttachment = async (attachment = null) => {
    const deleteAttachment = async () => {
      const response = await api.delete(
        `/policy-attachments/${attachment?.id}`
      );
      if (response.ok) {
        setPolicyAttachment({
          name: "",
          attachment: "",
          visible: true,
        });
        showAlert("Policy attachment deleted", "success");
        handleCloseAttachmentForm();
        toggleReload();
      }
    };
    Swal.fire({
      title: "Delete Attachment",
      text: "Are you sure you want to delete this attachment?",
      icon: "warning",
      allowOutsideClick: false,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes",
      showCancelButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        deleteAttachment();
      }
    });
  };

  useEffect(() => {
    setPolicy(props?.policy);
  }, [props?.policy]);
  //   console.log(attachments);

  return (
    <div className="table-responsive">
      <fieldset disabled={props?.readOnly ? true : false}>
        {attachments && attachments?.length > 0 ? (
          <table className="table table-striped align-middle">
            <thead>
              <tr>
                <th>Type</th>
                <th>Title</th>
                <th className="text-nowrap">Creation Date</th>
                {!hideAction && (
                  <>
                    <th>Created by</th>
                    <th className="text-center">Visible to Client</th>
                  </>
                )}
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {attachments?.length > 0 &&
                attachments.map((attachment, index) => (
                  <tr key={index}>
                    <td>
                      <span>{attachment?.mimetype}</span>
                    </td>
                    <td>
                      <span className="h6 text-truncate mb-0">
                        {attachment?.name}
                      </span>
                    </td>

                    <td>
                      <span className="text-nowrap text-muted">
                        {moment(attachment.createdAt).format("LL")}
                      </span>
                    </td>
                    {!hideAction && (
                      <>
                        <td>
                          <span className="btn-link text-nowrap">
                            {attachment?.User?.name || ""}
                          </span>
                        </td>
                        <td className="h5">
                          {attachment?.visible ? (
                            <div className="d-block badge bg-success">
                              Visible
                            </div>
                          ) : (
                            <div className="d-block badge bg-light">
                              Invisible
                            </div>
                          )}
                        </td>
                      </>
                    )}
                    <td>
                      <div className="text-nowrap text-center">
                        <a
                          href={attachment?.location}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          className="btn btn-icon btn-sm btn-info mx-1"
                        >
                          <i className="demo-pli-file-text-image fs-5"></i>
                        </a>
                        {!hideAction && (
                          <>
                            <a
                              href="#"
                              className="btn btn-icon btn-sm btn-success mx-1"
                              onClick={() => {
                                handleEditAttachment(attachment);
                              }}
                            >
                              <i className="demo-pli-pen-5 fs-5"></i>
                            </a>
                            <a
                              href="#"
                              className="btn btn-icon btn-sm btn-danger mx-1"
                              onClick={() => handleDeleteAttachment(attachment)}
                            >
                              <i className="demo-pli-trash fs-5"></i>
                            </a>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        ) : (
          <p>No attachments found</p>
        )}
      </fieldset>
      <Modal
        show={showAttachmentForm}
        onHide={handleCloseAttachmentForm}
        backdrop="static"
        keyboard={false}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Policy Attachments</Modal.Title>
        </Modal.Header>
        <Form autoComplete="off" onSubmit={saveAttachment}>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="formBasicEmail">
              <Form.Label>Attachment name</Form.Label>
              <Form.Control
                autoFocus
                type="text"
                placeholder="Enter name"
                required
                value={policyAttachment?.name || ""}
                onChange={(e) =>
                  setPolicyAttachment({
                    ...policyAttachment,
                    name: e.target.value,
                  })
                }
              />
              <Form.Text className="text-muted">
                Description of attachment
              </Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Check
                type="switch"
                id="custom-switch"
                label="Visible to client"
                checked={policyAttachment.visible == true ? true : false}
                onChange={(e) =>
                  setPolicyAttachment({
                    ...policyAttachment,
                    visible: e.target.checked,
                  })
                }
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => handleCloseAttachmentForm()}
            >
              Close
            </Button>
            <Button variant="primary" type="submit">
              Submit
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default ClientPolicyAttachment;
